import React from 'react';
import MainPage from '@app/component/page/MainPage';
import Message from '@app/component/message/Message';
import { useQuery } from '@tanstack/react-query';
import { ReviewService } from '@app/model/review/service/ReviewService';
import { ReviewsQueryKeys } from '@app/model/review/service/ReviewsQueryKeys';
import ReviewsPagePlaceholder from '@app/component/placeholder/ReviewsPagePlaceholder';
import _isEmpty from 'lodash/isEmpty';
import GeneralStatIndicatorPortlet
	from '@app/site/secured/review-manager/fragment/property-overview/component/GeneralStatIndicatorPortlet';
import Utilities from '@util/Utilities';
import {
	ArrowTrendingUpIcon,
	ChatBubbleBottomCenterTextIcon,
	CheckCircleIcon,
	StarIcon,
} from '@heroicons/react/20/solid';
import ReviewsGroupPropertyPerformanceFragment
	from '@app/site/secured/review-manager/fragment/group-overview/ReviewsGroupPropertyPerformanceFragment';
import ReviewsGroupSentimentDistributionGraph
	from '@app/site/secured/review-manager/fragment/group-overview/graph/ReviewsGroupSentimentDistributionGraph';
import ReviewsGroupPropertyPerformanceHeatmapFragment
	from '@app/site/secured/review-manager/fragment/group-overview/ReviewsGroupPropertyPerformanceHeatmapFragment';


const ReviewsGroupOverviewPage = React.memo(() => {

	const { data: groupReviewsState, isLoading, isSuccess } = useQuery({
		queryFn: () => ReviewService.findGroupReviewsStateForPeriod(),
		queryKey: ReviewsQueryKeys.groupReviewsStateForPeriod(),
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	if (isLoading)
		return <MainPage><ReviewsPagePlaceholder /></MainPage>;

	if (isSuccess && !_isEmpty(groupReviewsState)) {

		return <MainPage>
			<div className="mt-6">
				<div className="sm:flex items-center justify-between">
					<div className="flex items-center sm:space-x-5 mb-2 sm:mb-0">
						<h1 className="text-xl font-bold leading-6">Group Overview</h1>
					</div>
				</div>
			</div>
			<div className="mt-6">
				<dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
					<GeneralStatIndicatorPortlet
						title="Portfolio Score"
						stat={`${Utilities.formatDouble(groupReviewsState.averageScore(), 1)}/10`}
						icon={<StarIcon className="h-4 w-4 text-gray-400" />}
					/>
					<GeneralStatIndicatorPortlet
						title="Total Reviews"
						stat={`${groupReviewsState.totalNumberOfReviews()}`}
						icon={<ChatBubbleBottomCenterTextIcon className="h-4 w-4 text-gray-400" />}
					/>
					<GeneralStatIndicatorPortlet
						title="Avg. Response Rate"
						stat={`${Utilities.formatDouble(groupReviewsState.averageResponseRate() * 100, 1)}%`}
						icon={<CheckCircleIcon className="h-4 w-4 text-gray-400" />}
					/>
					<GeneralStatIndicatorPortlet
						title="Market Position"
						stat={`#${Utilities.formatDouble(groupReviewsState.averageRank(), 1)}`}
						icon={<ArrowTrendingUpIcon className="h-4 w-4 text-gray-400" />}
					/>
				</dl>
			</div>
			<div className="mt-8">
				<div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
					<ReviewsGroupSentimentDistributionGraph groupReviewsState={groupReviewsState} />
					<ReviewsGroupPropertyPerformanceFragment groupReviewsState={groupReviewsState} />
					<ReviewsGroupPropertyPerformanceHeatmapFragment groupReviewsState={groupReviewsState} />
				</div>
			</div>
		</MainPage>;
	}

	return <MainPage><Message state="error">Group Overview cannot be loaded at the moment. Please retry again
		later!</Message></MainPage>;
});


export default ReviewsGroupOverviewPage;
